import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Paper, Stack, Typography, Button, IconButton, Tooltip, TextField, Link,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  ToggleButton, ToggleButtonGroup, CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { notify } from "../notify";
import { LINKS_API, linkTarget, openLink } from "./LinkList";

const BLANK = { title: "", url: "", document: null };

// Move item at index `i` by `delta` (±1), returning a new array.
const moveItem = (list, i, delta) => {
  const j = i + delta;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

/**
 * Admin management of one curated link list — the Guidelines page's policy and
 * public lists, or one event's information links. Add / edit / remove entries and
 * change their display order; each entry points at either an uploaded document
 * (held in document storage) or an external URL.
 *
 * Props: kind ("policy" | "public" | "event"), eventId (required for "event"),
 * empty, addLabel, canAdd, canEdit, canDelete, dense, onSummary(text) — a
 * one-line description for a collapsed section.
 */
export default function LinkManager({ kind, eventId, empty, addLabel,
                                     canAdd, canEdit, canDelete, dense = false, onSummary }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | "new" | id
  const [form, setForm] = useState(BLANK);
  const [mode, setMode] = useState("document");   // target: "document" | "url"
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // link pending deletion

  // Every request identifies the list; the server gates reads/writes on it.
  const listParams = useCallback(() => (
    eventId === undefined ? { kind } : { kind, event_id: eventId }
  ), [kind, eventId]);

  // An `eventId` that's present but isn't an id means there's no list to ask for
  // yet: the Events dialog passes its `editing` state, which drops to null while
  // the dialog closes but stays mounted through the transition. Requesting an
  // event list without an event_id is a 422, so skip it.
  const identified = eventId === undefined || typeof eventId === "number";

  const load = useCallback(() => {
    // Drop the previous list and go back to loading, so reopening the dialog on a
    // different event doesn't flash that event's links or a false "none yet".
    if (!identified) { setLinks([]); setLoading(true); return Promise.resolve(); }
    return axios.get(LINKS_API + "/", { params: listParams() })
      .then(res => setLinks(res.data || []))
      .finally(() => setLoading(false));
  }, [identified, listParams]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (loading) return;
    onSummary?.(links.length
      ? `${links.length} link${links.length === 1 ? "" : "s"}: ${links.map(l => l.title).join(", ")}`
      : "None yet");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links, loading]);

  const openNew = () => {
    setForm(BLANK);
    setMode("document");
    setEditing("new");
  };

  const openEdit = (link) => {
    if (!canEdit) return;
    setForm({
      title: link.title || "",
      url: link.url || "",
      document: link.document_id
        ? { id: link.document_id, original_filename: link.document_filename }
        : null,
    });
    setMode(link.url ? "url" : "document");
    setEditing(link.id);
  };

  const close = () => { setEditing(null); setForm(BLANK); };

  const upload = (files) => {
    const file = files && files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    setBusy(true);
    axios.post(`${LINKS_API}/upload`, fd)
      .then(res => setForm(f => ({ ...f, document: res.data })))
      .finally(() => setBusy(false));
  };

  const save = () => {
    const payload = {
      title: form.title.trim(),
      url: mode === "url" ? form.url.trim() : null,
      document_id: mode === "document" ? form.document.id : null,
    };
    const req = editing === "new"
      ? axios.post(LINKS_API + "/", { ...payload, ...listParams() })
      : axios.put(`${LINKS_API}/${editing}`, payload);
    setBusy(true);
    req.then(() => {
      notify(editing === "new" ? "Link added." : "Link saved.", "success");
      load();
      close();
    }).finally(() => setBusy(false));
  };

  const doDelete = () => {
    setBusy(true);
    axios.delete(`${LINKS_API}/${confirmDelete.id}`)
      .then(() => {
        notify("Link removed.", "success");
        setConfirmDelete(null);
        if (editing === confirmDelete.id) close();
        load();
      })
      .finally(() => setBusy(false));
  };

  // Reordering is persisted straight away (no save step) — the whole list's new
  // id order is sent, and the server rewrites sort_order from it.
  const move = (i, delta) => {
    const next = moveItem(links, i, delta);
    if (next === links) return;
    setLinks(next);  // optimistic, so the row moves without waiting
    axios.put(`${LINKS_API}/reorder`, { ...listParams(), ids: next.map(l => l.id) })
      .then(res => setLinks(res.data || []))
      .catch(() => load());
  };

  const canSave = form.title.trim().length > 0
    && (mode === "url" ? form.url.trim().length > 0 : !!form.document);
  const showActions = canEdit || canDelete;

  return (
    <Box>
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                <TableCell>Title</TableCell>
                <TableCell>Document / link</TableCell>
                {showActions && <TableCell align="right">Order</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {links.map((link, i) => (
                <TableRow hover key={link.id} onClick={() => openEdit(link)}
                          sx={{ cursor: canEdit ? "pointer" : "default" }}>
                  <TableCell>{link.title}</TableCell>
                  <TableCell>
                    <Link component="button" type="button"
                          onClick={(e) => { e.stopPropagation(); openLink(link); }}
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, textAlign: "left" }}>
                      {linkTarget(link)}
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </TableCell>
                  {showActions && (
                    <TableCell align="right" padding="checkbox">
                      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
                        {canEdit && (
                          <>
                            <Tooltip title="Move up"><span>
                              <IconButton size="small" disabled={i === 0}
                                          aria-label={`Move ${link.title} up`}
                                          onClick={(e) => { e.stopPropagation(); move(i, -1); }}>
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton></span>
                            </Tooltip>
                            <Tooltip title="Move down"><span>
                              <IconButton size="small" disabled={i === links.length - 1}
                                          aria-label={`Move ${link.title} down`}
                                          onClick={(e) => { e.stopPropagation(); move(i, 1); }}>
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton></span>
                            </Tooltip>
                          </>
                        )}
                        {canDelete && (
                          <Tooltip title="Remove"><span>
                            <IconButton size="small" color="error"
                                        aria-label={`Remove ${link.title}`}
                                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(link); }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton></span>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {links.length === 0 && (
                <TableRow>
                  <TableCell colSpan={showActions ? 3 : 2} align="center"
                             sx={{ color: "text.secondary", py: dense ? 2 : 3 }}>
                    {loading ? <CircularProgress size={24} /> : empty}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {canAdd && (
        <Button size={dense ? "small" : "medium"} startIcon={<AddIcon />} onClick={openNew}
                sx={{ mt: 1 }}>{addLabel}</Button>
      )}

      {/* Add / edit dialog */}
      <Dialog open={editing !== null} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>{editing === "new" ? addLabel : "Edit link"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" fullWidth required autoFocus value={form.title}
                       onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                       helperText="Shown to users as the link text." />
            <ToggleButtonGroup size="small" exclusive value={mode} aria-label="Link target"
                               onChange={(_, m) => m && setMode(m)}>
              <ToggleButton value="document">Uploaded document</ToggleButton>
              <ToggleButton value="url">External link</ToggleButton>
            </ToggleButtonGroup>
            {mode === "document" ? (
              <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Button variant="outlined" component="label" disabled={busy}
                        startIcon={busy ? <CircularProgress size={16} /> : <UploadFileIcon />}>
                  {form.document ? "Replace document" : "Upload document"}
                  <input hidden type="file" onChange={(e) => upload(e.target.files)} />
                </Button>
                <Typography variant="body2" color={form.document ? "text.primary" : "text.secondary"}>
                  {form.document ? form.document.original_filename : "No document uploaded"}
                </Typography>
              </Stack>
            ) : (
              <TextField label="URL" fullWidth value={form.url}
                         onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
                         placeholder="https://www.resus.org.uk/..."
                         helperText="Opens in a new tab." />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button variant="contained" disabled={!canSave || busy} onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Remove link?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove "{confirmDelete?.title}" from the list
            {confirmDelete?.document_id ? " and delete the uploaded document" : ""}. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={busy}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />}
                  onClick={doDelete} disabled={busy}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
